import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, MOTION_QUERIES, ScrollTrigger, useGSAP } from '@/lib/motion'

interface MarqueeProps {
    children: ReactNode
    // Seconds for one full loop at rest.
    duration?: number
    reverse?: boolean
    className?: string
    trackClassName?: string
}

// An endless row that drifts on its own, follows the direction of the page
// scroll and surges with scroll speed. Content is rendered twice so the loop
// is seamless; the copy is hidden from assistive technology.
const Marquee: FC<MarqueeProps> = ({
    children,
    duration = 40,
    reverse = false,
    className,
    trackClassName
}): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const root = rootRef.current
        const track = trackRef.current

        if (!root || !track) return

        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.motion, () => {
            const setX = gsap.quickSetter(track, 'x', 'px')
            const base = reverse ? 1 : -1
            let direction = 1
            let boost = 0
            let x = 0

            const tick = (_time: number, deltaTime: number): void => {
                const half = track.scrollWidth / 2

                if (!half) return

                const speed = half / duration
                x += base * direction * speed * (deltaTime / 1000) * (1 + boost)
                boost *= 0.93
                x = ((x % half) - half) % half
                setX(x)
            }

            const trigger = ScrollTrigger.create({
                trigger: root,
                start: 'top bottom',
                end: 'bottom top',
                onToggle: (self) => {
                    if (self.isActive) gsap.ticker.add(tick)
                    else gsap.ticker.remove(tick)
                },
                onUpdate: (self) => {
                    direction = self.direction
                    boost = Math.min(Math.abs(self.getVelocity()) / 260, 7)
                }
            })

            if (trigger.isActive) gsap.ticker.add(tick)

            return () => {
                gsap.ticker.remove(tick)
                trigger.kill()
            }
        })

        return () => mm.revert()
    }, { scope: rootRef })

    return (
        <div ref={rootRef} className={cn('overflow-hidden', className)}>
            <div ref={trackRef} className={cn('flex w-max will-change-transform', trackClassName)}>
                <div className='flex shrink-0 items-center'>
                    {children}
                </div>

                <div aria-hidden='true' className='flex shrink-0 items-center'>
                    {children}
                </div>
            </div>
        </div>
    )
}

export default Marquee
